import { useState } from 'react';
import type { TableInputDefinition } from '@arcanum-hq/shared-types';
import { TABLE_EDIT_PREFIX_RE } from './chat-thread-grouping';
import {
  hydrateTableGrid,
  computeColumnSums,
  type HydratedGrid,
} from './tool-results/hydrate-table-grid';
import { formatCellDisplay } from './tool-results/TableGridEditor';

/**
 * `<TableEditReceipt>` — end-of-thread receipt for an applied table edit
 * (table-input-ux immutable-history rework, 2026-06-08).
 *
 * The `POST /api/conversations/:id/table-edit` endpoint appends a
 * `[table-edit:${table}] N cell(s) applied via table edit` SystemMessage to the
 * LangGraph thread. `<ChatThread>` detects the prefix and renders THIS card
 * instead of the generic single-line system row — a point-in-time confirmation
 * that the edit landed, plus an "Edit" affordance.
 *
 * **Full-card chrome (S3 upgrade)**: when `tableDef` + `inputs` are provided
 * this component renders the `FrozenTableCard` chrome from the design prototype
 * (R5 fidelity): a section-label header (`Updated · <TableName>`) + accent
 * "edited" status dot + Edit button, followed by a full read-only table showing
 * the current model values per row × column.
 *
 * **Compact fallback**: when `tableDef` is absent (e.g. schema not yet loaded,
 * or a public share view without the model schema) the component falls back to
 * the prior single-line receipt row — preserving backward compatibility with
 * every existing test that does not pass `tableDef`.
 *
 * "Edit" dispatches the `arcanum:edit-table` window event; the composer layer
 * (Workbench composer-mode swap, S2) listens and opens `<InteractionSurface>`
 * in interaction mode, seeded from current model values. The receipt itself
 * stays in place (immutable transcript).
 */

export const EDIT_TABLE_EVENT = 'arcanum:edit-table';

export interface EditTableEventDetail {
  table: string;
  conversationId: string;
}

interface TableEditReceiptProps {
  /** Raw system-message content, e.g. `[table-edit:UnitMix] 5 cells applied via table edit`. */
  content: string;
  /** Conversation ID — carried in the edit-table event payload. */
  conversationId?: string | null;
  /** Resolved human-readable table name (falls back to the parsed key). */
  tableDisplayName?: string;
  /**
   * Full TableInputDefinition from the model schema.
   * When present (together with `inputs`), renders the full FrozenTableCard chrome.
   * When absent, renders the compact single-line fallback.
   */
  tableDef?: TableInputDefinition | null;
  /**
   * LIVE model values (`useConversationViews().inputs`). Used to render the
   * grid for the LATEST receipt only — it reflects current model state.
   */
  inputs?: Record<string, { value: unknown; source?: string }> | undefined;
  /**
   * FROZEN point-in-time values for THIS edit (`namedRange → { value }`, from
   * the breadcrumb's `tableSnapshot`). Used to render the grid for NON-latest
   * receipts so history doesn't change when a later edit lands. Absent on
   * legacy receipts (pre-2026-06-09) → a non-latest receipt with no snapshot
   * degrades to the compact single-line fallback rather than showing stale
   * live values. (user feedback 2026-06-09: tables must be frozen in time.)
   */
  frozenInputs?:
    | Record<string, { value: unknown; source?: string }>
    | undefined;
  /**
   * The PRIOR receipt's values for this table (`namedRange → { value }`), from
   * `<ChatThread>`. When present, the receipt shows an expandable "what changed"
   * diff enumerating the cells that differ from the previous edit. Absent on the
   * first edit of a table → no diff shown. (user feedback 2026-06-09.)
   */
  previousInputs?:
    | Record<string, { value: unknown; source?: string }>
    | undefined;
  /**
   * Whether this is the LATEST receipt for its table. The latest is the only
   * editable one (older = immutable history) AND the only one that shows LIVE
   * values + a LIVE badge. The caller (`<ChatThread>`) computes this per table.
   * Defaults to `true` so direct/standalone renders (tests, public views) keep
   * the affordance + live behavior.
   */
  isLatest?: boolean;
}

/** Parse the `[table-edit:<table>] <summary>` breadcrumb. */
export function parseTableEditMessage(
  content: string
): { table: string; summary: string } | null {
  const match = TABLE_EDIT_PREFIX_RE.exec(content);
  if (!match) return null;
  return {
    table: match[1],
    summary: content.replace(TABLE_EDIT_PREFIX_RE, '').trim(),
  };
}

export function TableEditReceipt({
  content,
  conversationId,
  tableDisplayName,
  tableDef,
  inputs,
  frozenInputs,
  previousInputs,
  isLatest = true,
}: TableEditReceiptProps) {
  const [showChanges, setShowChanges] = useState(false);
  const parsed = parseTableEditMessage(content);
  if (!parsed) return null;

  const displayName = tableDisplayName ?? parsed.table;
  // `summary` reads e.g. "5 cells applied via table edit"; trim the trailing
  // "via table edit" boilerplate for a tighter receipt line.
  const summary = parsed.summary.replace(/\s*via table edit$/, '');

  function handleEdit() {
    if (!conversationId) return;
    window.dispatchEvent(
      new CustomEvent<EditTableEventDetail>(EDIT_TABLE_EVENT, {
        detail: { table: parsed!.table, conversationId },
      })
    );
  }

  // Only the latest receipt for a table is editable (immutable history).
  const canEdit = !!conversationId && isLatest;

  // Value source: the LATEST receipt shows LIVE model values; older receipts
  // show their FROZEN point-in-time snapshot so history doesn't shift when a
  // later edit lands.
  const valuesSource = isLatest ? inputs : frozenInputs;
  // Full card requires a table schema AND a values source — the latest always
  // qualifies (live inputs, or schema defaults if not yet loaded); a non-latest
  // receipt qualifies only when it carries a frozen snapshot. A legacy
  // non-latest receipt (no snapshot) falls through to the compact line.
  const showFullCard = !!tableDef && (isLatest || !!frozenInputs);

  if (tableDef && showFullCard) {
    const grid: HydratedGrid = hydrateTableGrid(
      tableDef,
      valuesSource,
      undefined
    );
    // Generic per-column totals from the table's `sum` aggregates (e.g. Total
    // Units). Empty for tables without sum aggregates → no total row.
    const columnSums = computeColumnSums(tableDef, grid.rows);
    const hasTotals = Object.keys(columnSums).length > 0;

    // "What changed" diff vs the previous receipt: enumerate editable cells
    // whose value differs from the prior edit. (user feedback 2026-06-09.)
    const changes = previousInputs
      ? grid.rows.flatMap((row) =>
          row.cells.flatMap((cell) => {
            if (!cell.editable) return [];
            const prevVal = previousInputs[cell.namedRange]?.value as
              | number
              | string
              | boolean
              | undefined;
            if (prevVal === undefined) return [];
            const newVal = cell.displayValue;
            if (Number(prevVal) === Number(newVal)) return [];
            const col = tableDef.columns.find((c) => c.key === cell.colKey);
            return [
              {
                label: `${row.rowLabel} · ${col?.displayName ?? cell.colKey}`,
                from: formatCellDisplay(prevVal, cell.dataType),
                to: formatCellDisplay(newVal, cell.dataType),
              },
            ];
          })
        )
      : [];
    return (
      <div
        className="mb-4 max-w-[68%] ml-8"
        data-testid={`table-edit-receipt-${parsed.table}`}
      >
        {/* Header row: section label + status dot (descriptive update text
            ONLY — the Edit affordance lives on the table itself, below, so it
            reads as "edit this table" rather than an action on the summary). */}
        <div className="flex items-center justify-between mb-2">
          <span className="flex items-center gap-2">
            <span className="text-[9px] font-mono font-semibold tracking-[1.2px] uppercase text-text-muted">
              Updated &middot; {displayName}
            </span>
            {isLatest ? (
              // The only receipt that tracks the live model — the rest are
              // frozen history. (user feedback 2026-06-09.)
              <span
                data-testid={`table-edit-receipt-live-${parsed.table}`}
                className="text-[8px] font-mono font-semibold tracking-[0.8px] uppercase text-accent border border-accent/40 rounded-[2px] px-1 py-0.5 leading-none"
              >
                Live
              </span>
            ) : (
              <span className="text-[8px] font-mono font-semibold tracking-[0.8px] uppercase text-text-dim border border-panel-border rounded-[2px] px-1 py-0.5 leading-none">
                Frozen
              </span>
            )}
          </span>
          <span className="flex items-center gap-1.5 text-[10px] font-mono text-accent">
            <span
              className="w-1.5 h-1.5 rounded-full bg-accent shrink-0"
              style={{ boxShadow: '0 0 4px rgba(0,200,83,0.4)' }} // @design-token glow has no token
            />
            {summary}
          </span>
        </div>

        {/* Full read-only table: columns × rows with current model values */}
        <div className="overflow-x-auto">
          <table className="text-[11px] font-mono tabular-nums border-collapse">
            <thead>
              <tr className="border-b border-panel-border">
                {/* Row-label column (empty header) */}
                <th className="px-2.5 py-1.5 text-left text-text-muted font-semibold uppercase tracking-[0.5px] text-[9px]" />
                {tableDef.columns.map((col) => (
                  <th
                    key={col.key}
                    className="px-2.5 py-1.5 text-left text-text-muted font-semibold uppercase tracking-[0.5px] text-[9px]"
                  >
                    {col.displayName}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grid.rows.map((row) => (
                <tr
                  key={row.rowLabel}
                  className="border-b border-panel-border/50"
                >
                  <td className="px-2.5 py-1.5 text-text-bright font-semibold">
                    {row.rowLabel}
                  </td>
                  {row.cells.map((cell) => (
                    <td
                      key={cell.colKey}
                      className="px-2.5 py-1.5 text-text-muted"
                    >
                      {formatCellDisplay(cell.displayValue, cell.dataType)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            {hasTotals && (
              <tfoot>
                <tr
                  className="border-t border-panel-border"
                  data-testid={`table-edit-receipt-total-${parsed.table}`}
                >
                  <td className="px-2.5 py-1.5 text-text-muted font-semibold uppercase tracking-[0.5px] text-[9px]">
                    Total
                  </td>
                  {tableDef.columns.map((col) => (
                    <td
                      key={col.key}
                      className="px-2.5 py-1.5 text-text-bright font-semibold tabular-nums"
                    >
                      {col.key in columnSums
                        ? formatCellDisplay(columnSums[col.key], col.dataType)
                        : ''}
                    </td>
                  ))}
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* Expandable "what changed" — enumerate cell diffs vs the prior edit. */}
        {changes.length > 0 && (
          <div className="mt-1.5">
            <button
              type="button"
              onClick={() => setShowChanges((v) => !v)}
              data-testid={`table-edit-receipt-changes-toggle-${parsed.table}`}
              className="text-[10px] font-mono text-text-muted hover:text-text-bright transition-colors cursor-pointer"
            >
              {showChanges ? '▾' : '▸'} {changes.length} change
              {changes.length !== 1 ? 's' : ''}
            </button>
            {showChanges && (
              <ul
                className="mt-1 space-y-0.5"
                data-testid={`table-edit-receipt-changes-${parsed.table}`}
              >
                {changes.map((ch, i) => (
                  <li
                    key={i}
                    className="text-[10px] font-mono flex items-center gap-1.5"
                  >
                    <span className="text-text-bright">{ch.label}</span>
                    <span className="text-text-dim">{ch.from}</span>
                    <span className="text-text-dim">&rarr;</span>
                    <span className="text-accent">{ch.to}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Edit affordance attached to the table — only on the latest receipt.
            Clicking opens the edit form in the composer ("the helper"). */}
        {canEdit && (
          <div className="flex justify-end mt-2">
            <button
              type="button"
              onClick={handleEdit}
              data-testid={`table-edit-receipt-edit-${parsed.table}`}
              className="text-[11px] font-mono text-accent hover:text-accent/80 transition-colors cursor-pointer border border-accent/30 hover:border-accent/60 rounded-[2px] px-2.5 py-1"
            >
              Edit table
            </button>
          </div>
        )}
      </div>
    );
  }

  // Compact fallback: single-line receipt row (when tableDef is absent).
  return (
    <div
      className="ml-8 max-w-[65%] flex items-center justify-between gap-3 px-2 py-1"
      data-testid={`table-edit-receipt-${parsed.table}`}
    >
      <div className="flex items-center gap-2 min-w-0">
        {/* Accent pulse dot — confirmed state signal */}
        <span className="w-1.5 h-1.5 rounded-full bg-accent shrink-0" />
        <span className="text-xs font-mono text-text-muted truncate">
          {displayName} &middot; {summary}
        </span>
      </div>
      {canEdit && (
        <button
          type="button"
          onClick={handleEdit}
          data-testid={`table-edit-receipt-edit-${parsed.table}`}
          className="text-xs font-mono text-accent/70 hover:text-accent transition-colors shrink-0"
        >
          Edit
        </button>
      )}
    </div>
  );
}
